/**
 * Data layer for `igaming-kyc-hooks-external-for-player-vetting`. Thin typed fetch wrappers over /api/v1/igaming-kyc-hooks-external-for-player-vetting.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingKycHooksExternalForPlayerVettingRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingKycHooksExternalForPlayerVettingResponse {
	count: number;
	items: IgamingKycHooksExternalForPlayerVettingRow[];
}

export interface CreateIgamingKycHooksExternalForPlayerVettingBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-kyc-hooks-external-for-player-vetting";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingKycHooksExternalForPlayerVettingResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingKycHooksExternalForPlayerVettingResponse>(res);
}

export async function create(body: CreateIgamingKycHooksExternalForPlayerVettingBody): Promise<IgamingKycHooksExternalForPlayerVettingRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingKycHooksExternalForPlayerVettingRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingKycHooksExternalForPlayerVettingQueryKeys = {
	all: ["igaming-kyc-hooks-external-for-player-vetting"] as const,
	list: () => [...igamingKycHooksExternalForPlayerVettingQueryKeys.all] as const,
	detail: (id: string) => [...igamingKycHooksExternalForPlayerVettingQueryKeys.all, id] as const,
};
